import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui';
import Button from './ui/Button';

const CopingResources = ({ compact = false, onResourceClick }) => {
  const navigate = useNavigate();
  
  const resources = [
    {
      id: 'safety_plan',
      title: 'My Safety Plan',
      description: 'Access your personalized crisis response plan',
      icon: '🛡️',
      color: 'blue',
      action: () => {
        if (onResourceClick) onResourceClick('safety_plan');
        navigate('/safety-plan');
      }
    },
    {
      id: 'grounding',
      title: 'Grounding Exercises',
      description: '5-4-3-2-1 technique and other grounding methods',
      icon: '🧘',
      color: 'green',
      content: (
        <div className="space-y-3 text-sm">
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">5-4-3-2-1 Grounding Technique</h4>
            <ol className="list-decimal list-inside space-y-1 text-gray-700">
              <li><strong>5 things you can see</strong> - Look around and name them</li>
              <li><strong>4 things you can touch</strong> - Notice textures</li>
              <li><strong>3 things you can hear</strong> - Listen carefully</li>
              <li><strong>2 things you can smell</strong> - Find scents nearby</li>
              <li><strong>1 thing you can taste</strong> - Notice any taste</li>
            </ol>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Box Breathing</h4>
            <p className="text-gray-700">
              Breathe in for 4 counts → Hold for 4 → Breathe out for 4 → Hold for 4 → Repeat
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'distress_tolerance',
      title: 'Distress Tolerance Skills',
      description: 'DBT techniques for managing intense emotions',
      icon: '💪',
      color: 'purple',
      content: (
        <div className="space-y-3 text-sm">
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">TIPP Skills (Quick Relief)</h4>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li><strong>Temperature</strong> - Hold ice or splash cold water on face</li>
              <li><strong>Intense exercise</strong> - 10 jumping jacks or quick walk</li>
              <li><strong>Paced breathing</strong> - Slow, deliberate breaths</li>
              <li><strong>Progressive muscle relaxation</strong> - Tense and release</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Self-Soothing with Senses</h4>
            <p className="text-gray-700">
              Engage any sense: listen to calming music, smell essential oils, 
              touch soft fabric, taste tea, look at comforting images.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'crisis_lines',
      title: 'Crisis Support Lines',
      description: 'Immediate help from trained counselors',
      icon: '☎️',
      color: 'red',
      content: (
        <div className="space-y-3 text-sm">
          <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
            <h4 className="font-semibold text-red-900 mb-2">24/7 Crisis Lines</h4>
            <ul className="space-y-2 text-red-800">
              <li>
                <strong>988 Suicide & Crisis Lifeline</strong><br />
                <span className="text-red-700">Call or text 988 (available 24/7)</span>
              </li>
              <li>
                <strong>Crisis Text Line</strong><br />
                <span className="text-red-700">Text "HELLO" to 741741</span>
              </li>
              <li>
                <strong>SAMHSA National Helpline</strong><br />
                <span className="text-red-700">1-800-662-4357 (free, confidential, 24/7)</span>
              </li>
            </ul>
          </div>
          <p className="text-gray-700 font-medium">
            If you're in immediate danger, call 911 or go to the nearest emergency room.
          </p>
        </div>
      )
    },
    {
      id: 'journaling',
      title: 'Immediate Journaling',
      description: 'Write through the moment without judgment',
      icon: '📝',
      color: 'yellow',
      content: (
        <div className="space-y-3 text-sm">
          <h4 className="font-semibold text-gray-900 mb-2">Quick Prompts for Right Now:</h4>
          <ul className="list-disc list-inside space-y-1 text-gray-700">
            <li>What am I feeling in my body right now?</li>
            <li>What triggered this feeling?</li>
            <li>What's the worst that could happen if I don't use?</li>
            <li>What's one thing I can do in the next 5 minutes?</li>
            <li>Who could I reach out to right now?</li>
            <li>What will I be proud of tomorrow if I get through this?</li>
          </ul>
        </div>
      )
    },
    {
      id: 'emergency_contacts',
      title: 'Emergency Contacts',
      description: 'Reach out to your support network',
      icon: '🆘',
      color: 'orange',
      action: () => {
        if (onResourceClick) onResourceClick('emergency_contacts');
        navigate('/safety-plan');
      }
    }
  ];
  
  const [expandedResource, setExpandedResource] = React.useState(null);
  
  const toggleResource = (resourceId) => {
    setExpandedResource(expandedResource === resourceId ? null : resourceId);
  };
  
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 hover:border-blue-400',
    green: 'bg-green-50 border-green-200 hover:border-green-400',
    purple: 'bg-purple-50 border-purple-200 hover:border-purple-400',
    red: 'bg-red-50 border-red-200 hover:border-red-400',
    yellow: 'bg-yellow-50 border-yellow-200 hover:border-yellow-400',
    orange: 'bg-orange-50 border-orange-200 hover:border-orange-400'
  };
  
  if (compact) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {resources.slice(0, 6).map((resource) => (
          <button
            key={resource.id}
            onClick={() => resource.action ? resource.action() : toggleResource(resource.id)}
            className={`p-4 border-2 rounded-lg transition-all ${colorClasses[resource.color]}`}
          >
            <div className="text-3xl mb-2">{resource.icon}</div>
            <div className="font-semibold text-gray-900 text-sm">{resource.title}</div>
          </button>
        ))}
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Immediate Coping Resources</h2>
        <p className="text-gray-600">
          These tools are here to help you through difficult moments. Click any resource to learn more.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {resources.map((resource) => (
          <Card
            key={resource.id}
            className={`border-2 cursor-pointer transition-all ${colorClasses[resource.color]}`}
            onClick={() => resource.action ? resource.action() : toggleResource(resource.id)}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <div className="text-4xl">{resource.icon}</div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-1">{resource.title}</h3>
                  <p className="text-sm text-gray-600">{resource.description}</p>
                </div>
              </div>
              {resource.content && (
                <div className="text-gray-400 ml-2">
                  {expandedResource === resource.id ? '▼' : '▶'}
                </div>
              )}
            </div>
            
            {resource.content && expandedResource === resource.id && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                {resource.content}
              </div>
            )}
            
            {resource.action && (
              <div className="mt-3">
                <Button size="sm" variant="primary" fullWidth>
                  Access Now →
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>
      
      <div className="mt-8 p-6 bg-gradient-to-r from-teal-50 to-blue-50 border-l-4 border-teal-500 rounded-lg">
        <h3 className="font-bold text-gray-900 mb-2">Remember:</h3>
        <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
          <li>Difficult urges are temporary - they peak and pass</li>
          <li>You've gotten through every difficult moment so far</li>
          <li>Reaching out is strength, not weakness</li>
          <li>One moment at a time is enough</li>
        </ul>
      </div>
    </div>
  );
};

export default CopingResources;
